import { useMemo, useState } from 'react';
import { useAuth } from '@contexts/useAuth';
import type { ChannelSummary, CommsMessage, MessageSegment } from '@interfaces/ICommsApi';
import { ChatHeader } from '../ChatHeader/ChatHeader';
import { MessageList } from '../MessageList/MessageList';
import { MessageInfoDialog } from '../MessageInfo/MessageInfoDialog';
import { Composer } from '../Composer/Composer';
import { useChat } from '@d-orca/shared/client/hooks';
import { useDeleteMessage } from '../../hooks/useDeleteMessage';
import { useEditMessage } from '../../hooks/useEditMessage';
import { useDeleteChannel } from '../../hooks/useDeleteChannel';
import { channelLabel } from '../../utils/channelLabel';
import { memberName, withAuthorNames } from '../../utils/memberName';
import type { CommsMember } from '@apis/comms';
import styles from './ChatThread.module.css';

interface IChatThreadProps {
  channel: ChannelSummary;
  siteId: string;
  members: CommsMember[];
  channels: ChannelSummary[];
  unreadSinceAt: string | undefined; // ISO timestamp captured when the user opened this channel
  /** How many were unread when the user opened this channel — the "unread above" pill's count. */
  unreadAtOpenCount: number;
  /** True for a DM opened (via right-click or the create-conversation modal)
   *  that has no backend record yet — see CommsPage's virtual-channel resolution. */
  isVirtual: boolean;
  /** Open (or create a virtual placeholder for) a DM with this user id — bound
   *  to a message author's "Reply privately" context-menu entry. */
  onOpenDm: (userId: string) => void;
  onOpenChannel: (channelId: string) => void;
  onEdit: () => void;
  onDeleted: () => void;
}

export function ChatThread({
  channel,
  siteId,
  members,
  channels,
  unreadSinceAt,
  unreadAtOpenCount,
  isVirtual,
  onOpenDm,
  onOpenChannel,
  onEdit,
  onDeleted,
}: IChatThreadProps) {
  const { user } = useAuth();
  const { messages: rawMessages, send, retry, discard, reportRead, localAttachments, isLoadingHistory, hasMore, isFetchingMore, loadMore } = useChat(channel.id, { isVirtual });
  // The record open behind one of the user's own messages.
  const [infoMessage, setInfoMessage] = useState<CommsMessage | null>(null);
  const currentUserId = user?.id ?? 'me';
  const channelName = channelLabel(channel, currentUserId, members);
  const creator = channel.kind === 'channel' ? members.find((m) => m.id === channel.creatorId) : undefined;
  const createdByName = creator && memberName(creator);
  const messages = useMemo(() => withAuthorNames(rawMessages, members), [rawMessages, members]);
  const deleteMessage = useDeleteMessage(siteId);
  const editMessage = useEditMessage(siteId);
  const deleteChannel = useDeleteChannel(siteId);

  function handleDeleteMessage(message: CommsMessage) {
    deleteMessage.mutate({ channelId: channel.id, messageId: message.id });
  }

  function handleEditMessage(message: CommsMessage, segments: MessageSegment[]): Promise<void> {
    return new Promise((resolve, reject) => {
      editMessage.mutate(
        { channelId: channel.id, messageId: message.id, segments },
        { onSuccess: () => resolve(), onError: (err) => reject(err) },
      );
    });
  }

  function handleDeleteChannel() {
    deleteChannel.mutate(channel.id, { onSuccess: onDeleted });
  }

  return (
    <div className={styles.thread}>
      <ChatHeader
        channel={channel}
        label={channelName}
        memberCount={channel.memberCount}
        currentUserId={currentUserId}
        members={members}
        channels={channels}
        onEdit={onEdit}
        onDelete={handleDeleteChannel}
        onMessageMember={onOpenDm}
        onOpenChannel={onOpenChannel}
      />
      <MessageList
        key={channel.id}
        channel={channel}
        channelName={channelName}
        createdByName={createdByName}
        messages={messages}
        currentUserId={currentUserId}
        unreadSinceAt={unreadSinceAt}
        unreadAtOpenCount={unreadAtOpenCount}
        isLoadingHistory={isLoadingHistory}
        hasMore={hasMore}
        isFetchingMore={isFetchingMore}
        loadMore={loadMore}
        onRetry={retry}
        onDiscard={discard}
        localAttachments={localAttachments}
        onDelete={handleDeleteMessage}
        onEdit={handleEditMessage}
        onSendDirectMessage={onOpenDm}
        onRead={reportRead}
        onMessageInfo={setInfoMessage}
      />
      <MessageInfoDialog
        siteId={siteId}
        message={infoMessage}
        members={members}
        onClose={() => setInfoMessage(null)}
      />
      <Composer
        channelName={channelName}
        isDm={channel.kind === 'dm'}
        members={members}
        onSend={send}
      />
    </div>
  );
}
